import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import Paper, { PaperProps } from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import { TreeView } from '@mui/x-tree-view/TreeView';

import {
  getAllChildDepartmentIds,
  getChildDepartmentIdsToLevel,
  getDepartmentById,
} from 'src/features/departments/departments.utils';
import { ChevronDownIcon, ChevronUpIcon } from 'src/shared/components';

import { MultiDepartmentSelectorPaperExtraProps } from './MultiDepartmentSelector.types';

interface Props extends PaperProps, MultiDepartmentSelectorPaperExtraProps {}

export const MultiDepartmentSelectorPaper: FC<Props> = ({ onCheckedChange, departments, ids, children, ...props }) => {
  const theme = useTheme();

  const [expanded, setExpanded] = useState<string[]>([]);

  const handleSelect = (ev: SyntheticEvent, nodeIds: string[]) => {
    if (departments === undefined || ids === undefined) {
      return;
    }

    ev.preventDefault();

    if (
      ev.target instanceof HTMLElement &&
      (ev.target.nodeName === 'INPUT' || ev.target.nodeName === 'LABEL' || ev.target.nodeName === 'SPAN')
    ) {
      let idsCopy = [...ids];

      const selectedId = nodeIds[0];
      const selectedDepartment = getDepartmentById(departments, selectedId);
      if (!selectedDepartment) return;

      const childDepartmentIds = getAllChildDepartmentIds(selectedDepartment);

      const allChecked = childDepartmentIds.every((childId) => ids.includes(childId)) || idsCopy.includes(+selectedId);

      childDepartmentIds.forEach((childId) => {
        if ((allChecked && !ids.includes(childId)) || (!allChecked && ids.includes(childId))) {
          return;
        }

        if (allChecked) {
          idsCopy = idsCopy.filter((id) => id !== childId);
        } else {
          idsCopy = [...idsCopy, childId];
        }
      });

      onCheckedChange?.(idsCopy);
    }
  };

  const handleToggle = (ev: SyntheticEvent, nodeIds: string[]) => {
    if (
      ev.target instanceof Element &&
      (ev.target.nodeName === 'svg' ||
        ev.target.nodeName === 'path' ||
        ev.target.className === 'MuiTreeItem-iconContainer')
    ) {
      setExpanded(nodeIds);
    }
  };

  useEffect(() => {
    if (!departments) {
      return;
    }

    setExpanded(getChildDepartmentIdsToLevel(departments, departments.length > 1 ? 0 : 1));
  }, [departments]);

  return (
    <Paper {...props}>
      <TreeView
        defaultCollapseIcon={<ChevronUpIcon color={theme.palette.action.active} />}
        defaultExpandIcon={<ChevronDownIcon color={theme.palette.action.active} />}
        multiSelect
        expanded={expanded}
        onNodeSelect={handleSelect}
        onNodeToggle={handleToggle}
      >
        {children}
      </TreeView>
    </Paper>
  );
};
